import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import {
  type CompanyBlock,
  type PurchasingAccount,
  type PurchasingAccountUpdate,
  useCompany,
  useUpdatePurchasingAccount,
  useUpdateUpsAccount,
} from '@/api/company-settings'
import { InlineFieldGroup, type InlineGroupField } from '@/components/inline-field-group'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

const UPS_ACCOUNT = /^[A-Z0-9]{6}$/

type AccountField = Exclude<keyof PurchasingAccount, 'id'>

const ACCOUNT_FIELDS: ReadonlyArray<InlineGroupField<AccountField>> = [
  { key: 'name', label: 'Company name', required: true },
  { key: 'address1', label: 'Street', autoComplete: 'address-line1' },
  { key: 'address2', label: 'Suite, unit, building', autoComplete: 'address-line2' },
  { key: 'city', label: 'City', className: 'col-span-2', autoComplete: 'address-level2' },
  { key: 'state', label: 'State', className: 'col-span-2', autoComplete: 'address-level1' },
  { key: 'zip_code', label: 'ZIP', className: 'col-span-2', autoComplete: 'postal-code' },
  { key: 'country', label: 'Country', className: 'col-span-2', autoComplete: 'country' },
  { key: 'phone', label: 'Phone', className: 'col-span-4', inputMode: 'tel', autoComplete: 'tel' },
]

const BLOCKS: ReadonlyArray<{ block: CompanyBlock; label: string }> = [
  { block: 'bill_to', label: 'Bill to' },
  { block: 'buyer', label: 'Buyer' },
]

function accountValues(account: PurchasingAccount): Record<AccountField, string> {
  return Object.fromEntries(
    ACCOUNT_FIELDS.map(({ key }) => [key, account[key] ?? '']),
  ) as Record<AccountField, string>
}

/** The block as a PO prints it: name, street, city line, phone. */
function AccountAddress({ account }: { account: PurchasingAccount }) {
  const place = [
    [account.city, account.state].filter(Boolean).join(', '),
    account.zip_code,
  ]
    .filter(Boolean)
    .join(' ')
  const lines = [
    [account.address1, account.address2].filter(Boolean).join(', '),
    [place, account.country].filter(Boolean).join(', '),
    account.phone,
  ].filter(Boolean)
  return (
    <span className="block text-sm leading-6">
      <span className="block font-medium">{account.name}</span>
      {lines.map((line) => (
        <span key={line} className="block text-muted-foreground">
          {line}
        </span>
      ))}
    </span>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 px-5 py-4 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-6">
      <h2 className="pt-1 text-sm font-medium">{label}</h2>
      <div className="min-w-0 max-w-md">{children}</div>
    </div>
  )
}

/** Who we are on our purchase orders: Bill To, Buyer and the UPS account suppliers ship on (USA-1664). */
export function CompanySettingsPage() {
  const company = useCompany()
  const updateUps = useUpdateUpsAccount()
  const updateAccount = useUpdatePurchasingAccount()
  const [editing, setEditing] = useState<CompanyBlock | null>(null)

  const saveUpsAccount = (raw: string) => {
    const number = raw.trim().toUpperCase()
    if (number === company.data?.ups_account_number) return
    if (!UPS_ACCOUNT.test(number)) {
      toast.error('A UPS account number is 6 letters and numbers, like Y2641J')
      return
    }
    updateUps.mutate(number, {
      onSuccess: () => toast.success(`UPS account set to ${number}`),
      onError: () => toast.error('Could not save the UPS account number'),
    })
  }

  const saveAccount = (
    block: CompanyBlock,
    label: string,
    account: PurchasingAccount,
    draft: Record<AccountField, string>,
  ) => {
    const body: PurchasingAccountUpdate = {}
    for (const { key } of ACCOUNT_FIELDS) {
      const value = draft[key].trim()
      if (value !== (account[key] ?? '')) body[key] = value
    }
    updateAccount.mutate(
      { block, body },
      {
        onSuccess: () => {
          setEditing(null)
          toast.success(`${label} saved`)
        },
        onError: () => toast.error(`Could not save ${label.toLowerCase()}`),
      },
    )
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Company</h1>

      {company.isLoading ? (
        <div className="divide-y rounded-xl border bg-card">
          {[0, 1, 2].map((row) => (
            <div key={row} className="grid gap-2 px-5 py-4 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-6">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-12 w-56" />
            </div>
          ))}
        </div>
      ) : company.isError || !company.data ? (
        <div className="flex flex-col items-start gap-3 rounded-xl border p-5">
          <p className="text-sm font-medium">Company details could not be loaded.</p>
          <Button variant="outline" size="sm" onClick={() => company.refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <div className="divide-y rounded-xl border bg-card">
          {BLOCKS.map(({ block, label }) => {
            const account = company.data[block]
            return (
              <Row key={block} label={label}>
                <InlineFieldGroup
                  label={label}
                  fields={ACCOUNT_FIELDS}
                  values={accountValues(account)}
                  editing={editing === block}
                  onEditingChange={(open) => setEditing(open ? block : null)}
                  pending={updateAccount.isPending && updateAccount.variables?.block === block}
                  disabled={updateAccount.isPending}
                  onSave={(draft) => saveAccount(block, label, account, draft)}
                >
                  <AccountAddress account={account} />
                </InlineFieldGroup>
              </Row>
            )
          })}
          <Row label="UPS account">
            <POInlineEditableField
              value={company.data.ups_account_number}
              ariaLabel="UPS account number"
              title="Edit UPS account number"
              pending={updateUps.isPending}
              disabled={updateUps.isPending}
              showEditIcon
              buttonClassName="-ml-2 w-fit tabular-nums"
              inputClassName="max-w-40 uppercase"
              onConfirm={saveUpsAccount}
            />
          </Row>
        </div>
      )}
    </div>
  )
}
